import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ShieldCheck, Mail, MapPin, Phone, Lock, Database, Instagram } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function Footer() {
  const year = new Date().getFullYear();
  const { t } = useApp();

  return (
    <footer data-testid="site-footer" className="bg-[#F7F5EE] border-t border-amber-900/10 text-zinc-600">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          {/* Company Brand Column */}
          <div className="lg:col-span-4 space-y-5">
            <Logo className="text-zinc-900" />
            <p className="text-sm text-zinc-600 leading-relaxed max-w-sm">
              {t.footer.brandDesc}
            </p>

            <div className="flex flex-col gap-2.5 pt-2 text-xs text-zinc-700">
              <div className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 text-amber-600 shrink-0" />
                <span>{t.footer.locations}</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-amber-600 shrink-0" />
                <a href={`mailto:${t.footer.email}`} className="hover:text-zinc-950 transition-colors">
                  {t.footer.email}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 text-amber-600 shrink-0" />
                <a href="https://wa.me/6285183003076" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-950 transition-colors font-medium">
                  {t.footer.whatsapp}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Instagram className="h-4 w-4 text-amber-600 shrink-0" />
                <a href={t.footer.instagramUrl || "https://www.instagram.com/astaenterprise.id"} target="_blank" rel="noopener noreferrer" className="hover:text-zinc-950 transition-colors font-medium">
                  {t.footer.instagram || "@astaenterprise.id"}
                </a>
              </div>
            </div>
          </div>

          {/* Module Links */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-xs uppercase tracking-[0.2em] text-amber-800 font-bold">
              {t.footer.modulesTitle}
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li><a href="/#features" className="hover:text-zinc-950 transition-colors">{t.gallery.modules[2].title}</a></li>
              <li><a href="/#features" className="hover:text-zinc-950 transition-colors">{t.gallery.modules[1].title}</a></li>
              <li><a href="/#features" className="hover:text-zinc-950 transition-colors">{t.gallery.modules[3].title}</a></li>
              <li><a href="/#features" className="hover:text-zinc-950 transition-colors">{t.gallery.modules[4].title}</a></li>
              <li><a href="/#features" className="hover:text-zinc-950 transition-colors">{t.gallery.modules[5].title}</a></li>
              <li><a href="/#features" className="hover:text-zinc-950 transition-colors">{t.gallery.modules[6].title}</a></li>
              <li><a href="/#features" className="hover:text-zinc-950 transition-colors">{t.gallery.modules[7].title}</a></li>
            </ul>
          </div>

          {/* Ekosistem & Solusi */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xs uppercase tracking-[0.2em] text-amber-800 font-bold">
              {t.footer.ecosystemTitle}
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="hover:text-zinc-950 transition-colors">{t.nav.erp.title}</Link></li>
              <li><Link to="/academy" className="hover:text-zinc-950 transition-colors">{t.nav.academy.title}</Link></li>
              <li><Link to="/community" className="hover:text-zinc-950 transition-colors">{t.nav.community.title}</Link></li>
              <li><Link to="/media" className="hover:text-zinc-950 transition-colors">Media Lifestyle Gold</Link></li>
              <li><Link to="/books" className="hover:text-zinc-950 transition-colors">Playbook Bisnis Emas</Link></li>
            </ul>
          </div>

          {/* Trust & Security Badge Column */}
          <div className="lg:col-span-3 space-y-4">
            <h3 className="text-xs uppercase tracking-[0.2em] text-amber-800 font-bold">
              {t.footer.securityTitle}
            </h3>
            <div className="rounded-2xl border border-amber-200/80 bg-white p-5 space-y-3.5 shadow-sm">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-zinc-900">{t.footer.securityBadge1}</div>
                  <div className="text-[11px] text-zinc-600 mt-0.5">{t.footer.securityBadge1Desc}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Database className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-zinc-900">{t.footer.securityBadge2}</div>
                  <div className="text-[11px] text-zinc-600 mt-0.5">{t.footer.securityBadge2Desc}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-zinc-900">{t.footer.securityBadge3}</div>
                  <div className="text-[11px] text-zinc-600 mt-0.5">{t.footer.securityBadge3Desc}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-amber-900/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-600">
          <p>© {year} {t.footer.copyright}</p>
          <div className="flex items-center gap-6">
            <a href="/#faq" className="hover:text-zinc-950 transition-colors">{t.footer.helpFaq}</a>
            <a href="https://wa.me/6285183003076" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-950 transition-colors font-medium">{t.footer.contactSupport}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
